<script setup lang="ts">
import type { CalloutSize, CalloutVariant } from '~/utils/callout'
import { faXmark } from '@fortawesome/pro-regular-svg-icons'
import { ref } from 'vue'
import { CALLOUT_VARIANT_STYLES } from '~/utils/callout'

const props = withDefaults(defineProps<{
  variant: CalloutVariant
  title?: string
  dismissable?: boolean
  hideIcon?: boolean
  size?: CalloutSize
}>(), {
  size: 'xs',
})

const emit = defineEmits<{
  (event: 'close'): void
}>()

const open = ref(true)

const SIZES = {
  xs: '[&_*]:text-xs',
  sm: '[&_*]:text-sm [&_*]:leading-5.5',
} satisfies Record<CalloutSize, string>

const v = computed(() => CALLOUT_VARIANT_STYLES[props.variant])

const isAlert = computed(() => props.variant === 'error')

const callout = useTemplateRef('callout')

onMounted(() => {
  if (isAlert.value) {
    callout.value?.focus()
  }
})

function onClose({ target }: MouseEvent) {
  const button = target as HTMLElement
  const callout = button.parentNode as HTMLElement
  const nextElement = callout.nextElementSibling as HTMLElement | null
  open.value = false
  emit('close')
  nextElement?.focus()
}
</script>

<template>
  <aside v-if="open" ref="callout" class="flex items-start p-xs text-carbon-500 gap-2xs rounded-lg" :aria-label="v.label" :class="v.backgroundColor" :role="isAlert ? 'alert' : 'status'" :aria-live="isAlert ? 'assertive' : 'polite'" tabindex="-1">
    <Icon v-if="!hideIcon" :icon="v.icon" :class="v.color" />
    <div class="flex-1 flex flex-col items-start justify-center" :class="SIZES[size]">
      <p v-if="title" class="font-bold" v-text="title" />
      <slot aria-live="polite" />
    </div>
    <Button v-if="dismissable" :icon="faXmark" class="!p-0 !bg-transparent !text-blurple-500" shape="rounded" aria-label="Lukk melding" title="Lukk melding" @click="onClose" />
  </aside>
</template>
